import PropTypes from 'prop-types';
import { cssBackdropFilter, cssFilter } from '@/blocks/filter';
import { buildLogoStyle, logoFilterStyle, logoTint, resolveLogoSrc } from '@/theme/logo';

/**
 * A masked or original-colour graphic with the same CSS filters as a
 * fullscreen vector: the filter paints the mark, and invert-behind samples
 * the page through the mark instead of flipping the box around it.
 */
export default function FilteredMark({
    src,
    tint = true,
    color = 'var(--color-text)',
    filter,
    alt = '',
    className = 'block h-full w-full',
    style,
    maskSize = 'contain',
    maskPosition = 'center',
    imageClassName = 'block h-full w-full object-contain object-left',
    imageStyle,
}) {
    const backdrop = cssBackdropFilter(filter);
    const css = cssFilter(filter);
    const fillsParent = /\babsolute\b/.test(className);
    const mask = {
        maskImage: `url("${src}")`,
        maskSize,
        maskRepeat: 'no-repeat',
        maskPosition,
        WebkitMaskImage: `url("${src}")`,
        WebkitMaskSize: maskSize,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: maskPosition,
    };
    const filtered = css ? { filter: css } : null;

    return (
        <span
            role={alt ? 'img' : undefined}
            aria-label={alt || undefined}
            aria-hidden={alt ? undefined : true}
            className={fillsParent ? className : `relative ${className}`}
            style={style}
            title={alt || undefined}
        >
            {backdrop ? (
                <span
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        backdropFilter: backdrop,
                        WebkitBackdropFilter: backdrop,
                        ...filtered,
                        ...mask,
                    }}
                />
            ) : tint ? (
                <span
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        backgroundColor: color,
                        ...filtered,
                        ...mask,
                    }}
                />
            ) : (
                <img
                    src={src}
                    alt=""
                    draggable={false}
                    className={imageClassName}
                    style={{ ...imageStyle, ...filtered }}
                />
            )}
        </span>
    );
}

FilteredMark.propTypes = {
    src: PropTypes.string.isRequired,
    tint: PropTypes.bool,
    color: PropTypes.string,
    filter: PropTypes.object,
    alt: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
    maskSize: PropTypes.string,
    maskPosition: PropTypes.string,
    imageClassName: PropTypes.string,
    imageStyle: PropTypes.object,
};

/**
 * Site logo (page, media, or slideshow) painted with the brand filter.
 * Text wordmarks keep their own type styles and take the filter on the box.
 */
export function SiteLogoMark({ logo, context = 'page', className, style, maskPosition = 'left center' }) {
    const mode = logo?.mode || 'mask';
    if (mode === 'text') {
        return (
            <span
                className={className}
                style={{
                    ...buildLogoStyle(logo, context),
                    ...logoFilterStyle(logo?.filter),
                    ...style,
                }}
            >
                {logo?.text || ''}
            </span>
        );
    }
    return (
        <FilteredMark
            src={resolveLogoSrc(logo, context)}
            tint={mode !== 'image'}
            color={logoTint(context)}
            filter={logo?.filter}
            maskPosition={maskPosition}
            className={className}
            style={style}
        />
    );
}

SiteLogoMark.propTypes = {
    logo: PropTypes.object,
    context: PropTypes.oneOf(['page', 'media', 'slideshow']),
    className: PropTypes.string,
    style: PropTypes.object,
    maskPosition: PropTypes.string,
};
